/**
 * 認証コンテキスト
 * アプリケーション全体で認証状態を管理
 */

import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { User, AuthState, AuthContextType } from './authTypes';
import { AuthContext } from './authContextObj';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useQueryClient } from '@tanstack/react-query';

/**
 * ユーザー情報の型定義
 */
/**
 * 認証状態の型定義
 */
/**
 * 認証コンテキストの型定義
 */
// types are moved to `authTypes.ts`

/**
 * 認証コンテキスト
 */
// AuthContext is declared in `authContextObj.ts`

/**
 * 認証プロバイダーのProps
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * 認証プロバイダー
 * アプリケーション全体に認証状態を提供する
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // ログイン/ログアウト境界で TanStack Query のキャッシュを破棄するために使用する。
  // これをしないと、別アカウントでログインした際に前ユーザーのイベント一覧等が
  // 残ったまま表示されてしまう（AuthProvider は QueryClientProvider の内側にある）。
  const queryClient = useQueryClient();

  /**
   * 現在の認証状態をチェック
   * アプリ起動時とページリロード時に実行
   */
  const checkAuthStatus = async () => {
    try {
      const currentUser = await getCurrentUser();

      // JWTトークンからユーザー情報を取得
      // JWT ペイロードは動的プロパティ(custom:age, custom:locationなど)を含むため any を使用
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let attributes: Record<string, any> = {};
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;

        if (idToken) {
          // IDトークンのペイロードから属性を取得
          // ペイロードの型は実行時まで不明なため any を使用
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          attributes = idToken.payload as Record<string, any>;
        }
      } catch (sessionError) {
        console.warn('Failed to fetch auth session:', sessionError);
      }

      const user: User = {
        id: currentUser.userId,
        email: attributes.email || attributes['cognito:username'] || '',
        // IMPORTANT: Do not use Cognito/IdP-provided names for in-app display.
        // The app must use the service profile's displayName (editable in Profile).
        // Keep this empty so UI can rely on ProfileContext.displayName.
        displayName: '',
        // Cognito/IdP の name 属性は CompleteProfile の初期値として使用
        cognitoName: attributes.name || '',
        // カスタム属性があれば追加
        age: attributes['custom:age'] ? parseInt(attributes['custom:age']) : undefined,
        location: attributes['custom:location'],
        interests: attributes['custom:interests'],
        avatar: attributes.picture,
        // identities クレームが存在する場合は外部 IdP 経由のユーザー
        isFederated: Array.isArray(attributes.identities) && attributes.identities.length > 0,
      };

      setAuthState({
        user,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch {
      // ログインしていない場合
      setAuthState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  };

  /**
   * アプリ起動時に認証状態をチェック + Amplify Hub で SDK 状態と自動同期
   *
   * #128: Amplify SDK 側の signIn/signOut を直接呼ばれた場合でも React state を
   * 自動更新する。`AuthContext.logout` 経由でない呼び出し (パスワード変更後の
   * `signOut({ global: true })` など) でも `isAuthenticated` が遅延しないよう、
   * Hub の auth イベントをリッスンする。
   */
  useEffect(() => {
    checkAuthStatus();
    const onPop = () => {
      // When the user navigates with browser back/forward, re-check auth status
      // This ensures deleted accounts / cleared tokens immediately redirect
      checkAuthStatus();
    };
    window.addEventListener('popstate', onPop);

    const unsubscribeHub = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          // 別アカウントのログインで前ユーザーのキャッシュを引かないよう全消去。
          // tokenRefresh とは分離する（更新のたびにキャッシュを消したくない）。
          queryClient.clear();
          checkAuthStatus();
          break;
        case 'tokenRefresh':
          checkAuthStatus();
          break;
        case 'signedOut':
        case 'tokenRefresh_failure':
          queryClient.clear();
          setAuthState({ user: null, isLoading: false, isAuthenticated: false });
          break;
        default:
          break;
      }
    });

    return () => {
      window.removeEventListener('popstate', onPop);
      unsubscribeHub();
    };
    // queryClient は useQueryClient() が返す安定参照のため再購読は起きない
  }, [queryClient]);

  /**
   * メール/パスワードでログイン
   */
  const login = async (email: string, password: string) => {
    try {
      await amplifySignIn({
        username: email,
        password,
      });
      await checkAuthStatus();
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(getErrorMessage(error));
    }
  };

  /**
   * Googleでログイン
   */
  const loginWithGoogle = async (intent: 'login' | 'register' = 'login') => {
    try {
      // Persist the user's intent across the OAuth redirect so we can
      // decide how to handle a missing account after return.
      try {
        sessionStorage.setItem('oauth_intent', intent);
      } catch {
        // ignore storage errors
      }

      await signInWithRedirect({ provider: 'Google' });
      // リダイレクト後、自動的にcheckAuthStatusが呼ばれる
    } catch (error) {
      console.error('Google login error:', error);
      throw new Error(getErrorMessage(error));
    }
  };

  /**
   * 新規登録
   * @returns Cognito User ID
   */
  const register = async (
    email: string,
    password: string,
    displayName: string
  ): Promise<{ userId: string }> => {
    try {
      const result = await amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name: displayName,
          },
        },
      });

      // Cognito User IDを取得
      const userId = result.userId;

      if (!userId) {
        throw new Error('User ID not returned from Cognito');
      }

      // 注: メール確認が必要なため、ここではログインしない
      // メール確認後にユーザーがログインする
      return { userId };
    } catch (error) {
      console.error('Register error:', error);
      throw new Error(getErrorMessage(error));
    }
  };

  /**
   * ログアウト
   */
  const logout = async (options?: { useHostedUI?: boolean; global?: boolean }) => {
    const useHostedUI = options?.useHostedUI !== false; // default true
    const global = options?.global === true;
    try {
      if (useHostedUI) {
        // When using hosted UI sign-out, amplifySignOut may redirect the
        // browser to the Cognito hosted UI logout endpoint. In some edge
        // cases (misconfiguration or network failure) it may return
        // without redirecting — ensure local auth state is cleared in both
        // cases so the UI updates immediately.
        await amplifySignOut(global ? { global: true } : undefined);
        // Best-effort: clear any storage that might hold tokens as well.
        try {
          for (const key of Object.keys(localStorage)) {
            if (
              key.startsWith('CognitoIdentityServiceProvider') ||
              key.startsWith('aws.cognito.identity') ||
              key.includes('amplify')
            ) {
              localStorage.removeItem(key);
            }
          }
          for (const key of Object.keys(sessionStorage)) {
            if (key.includes('amplify') || key.startsWith('CognitoIdentityServiceProvider')) {
              try {
                sessionStorage.removeItem('oauth_intent');
              } catch {
                /* ignore */
              }
              sessionStorage.removeItem(key);
            }
          }
        } catch (clearError) {
          console.warn('Failed to clear storage during logout:', clearError);
        }
      } else {
        // Always clear Amplify session storage so that tokens are removed
        try {
          await amplifySignOut(global ? { global: true } : undefined);
        } catch (signOutError) {
          // ignore; best effort to clear session
          console.warn('amplifySignOut failed during logout (continue):', signOutError);
        }

        // Also clear any local/session storage keys related to Amplify to avoid stale tokens
        try {
          for (const key of Object.keys(localStorage)) {
            if (
              key.startsWith('CognitoIdentityServiceProvider') ||
              key.startsWith('aws.cognito.identity') ||
              key.includes('amplify')
            ) {
              localStorage.removeItem(key);
            }
          }
          for (const key of Object.keys(sessionStorage)) {
            if (key.includes('amplify') || key.startsWith('CognitoIdentityServiceProvider')) {
              sessionStorage.removeItem(key);
            }
          }
          try {
            sessionStorage.removeItem('oauth_intent');
          } catch {
            /* ignore */
          }
        } catch (clearError) {
          console.warn('Failed to clear storage during logout:', clearError);
        }

      }

      // 前ユーザーのサーバー状態(イベント一覧など)が残らないよう Query キャッシュを破棄。
      // signedOut Hub イベントでもクリアするが、hosted UI ログアウトは
      // ここで即リダイレクトすることがあり Hub が発火しない場合があるため、明示的にも消す。
      queryClient.clear();

      // Ensure local auth state is cleared in all cases so the UI reflects
      // the logged-out status immediately.
      setAuthState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error('ログアウトに失敗しました');
    }
  };

  /**
   * エラーメッセージを取得
   */
  const getErrorMessage = (error: unknown): string => {
    // unknown型のエラーを安全に処理
    if (typeof error !== 'object' || error === null) {
      return 'エラーが発生しました';
    }

    const err = error as { name?: string; code?: string; message?: string };
    const errorCode = err.name || err.code;
    const errorMessage = err.message || '';

    switch (errorCode) {
      case 'UserNotFoundException':
        return 'メールアドレスまたはパスワードが正しくありません';
      case 'NotAuthorizedException':
        return 'メールアドレスまたはパスワードが正しくありません';
      case 'UsernameExistsException':
        return 'このメールアドレスは既に登録されています';
      case 'InvalidPasswordException':
        // エラーメッセージから詳細を抽出
        if (errorMessage.includes('symbol')) {
          return 'パスワードは8文字以上で、大文字、小文字、数字、記号（!@#$%^&*など）を含む必要があります';
        }
        return 'パスワードは8文字以上で、大文字、小文字、数字、記号を含む必要があります';
      case 'NetworkError':
        return 'ネットワークエラーが発生しました。インターネット接続を確認してください';
      default:
        return '認証エラーが発生しました。もう一度お試しください';
    }
  };

  const value: AuthContextType = {
    ...authState,
    login,
    loginWithGoogle,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * 認証コンテキストを使用するカスタムフック
 * コンポーネント内で認証状態と操作にアクセスできる
 */
// This hook is now defined in `src/hooks/useAuth.tsx` to allow fast refresh
